require('./lineChartETFsGrowth.scss');

const PropTypes = require('prop-types');

const React= require('react');
const moment = require('moment');
const d3 = require('d3');
const isEqual = require('lodash/lang/isEqual');
const reduce = require('lodash/collection/reduce');
const { decideAxisDateLabel,
        yAxisFormatLabel,
        createTooltip } = require('./lineChartETFsGrowthHelpers.js');

const blackBox = props => {

    const {lineData, currencyId, width, height} = props;
    const PADDING = 40;

    return me => {

        const xaxisG = me.select('.line-chart-growth__xaxis');
        const yaxisG = me.select('.line-chart-growth__yaxis');
        const gridG = me.select('.line-chart-growth__grid');
        const lineG = me.select('.line-chart-growth__line');
        const areaG = me.select('.line-chart-growth__area');
        const tooltip = me.select('.line-chart-growth__tooltip');
        const lineDataArray = lineData.toJS();
        const maxDate = d3.max(lineDataArray, d => moment.utc(d.date));
        const minDate = d3.min(lineDataArray, d => moment.utc(d.date));
        const maxVal = d3.max(lineDataArray, d => +d.value);
        const minVal = d3.min(lineDataArray, d => +d.value);
        const xDomain = [ minDate, maxDate ];
        const yDomain = [ minVal, maxVal + maxVal/10 ];
        const xAxisFormat = decideAxisDateLabel(maxDate, minDate);
        const yAxisFormat = (d) => yAxisFormatLabel(d, currencyId);

        const leftPadding = maxVal > 999 ? 60 : 45;

        var bisectDate = d3.bisector(function(d) { return d.date; }).left;

        const yScale = d3.scale.linear()
            .domain(yDomain)
            .range([height - PADDING, PADDING]);

        const xScale = d3.time.scale()
            .domain(xDomain)
            .range([leftPadding * 2, width]);

        const yAxis = d3.svg.axis()
            .orient('left')
            .scale(yScale)
            .innerTickSize(-width)
            .outerTickSize(0)
            .tickPadding(1)
            .tickFormat(yAxisFormat);

        const xAxis = d3.svg.axis()
            .orient('bottom')
            .scale(xScale)
            .innerTickSize(-height+(PADDING*2))
            .outerTickSize(0)
            .tickPadding(1)
            .tickFormat(xAxisFormat);

        const lineArray = lineDataArray.map(item => [xScale(moment.utc(item.date)), yScale(item.value)] );

        const lineDataArrayMap = lineDataArray.map(item => {
            return {date: new Date(item.date), value: item.value}
        });

        const drawLine = d3.svg.line()
                .interpolate('linear');

        const prepareTransition = d3.svg.line()
                .y(() => { return height - PADDING; })
                .interpolate('linear');

        const x = dateString => xScale(moment.utc(dateString));

        const y = amount => yScale(amount);

        const drawArea = d3.svg.area()
                .y0(height - PADDING)
                .interpolate('linear');

        gridG.selectAll('line').remove();
        areaG.selectAll('path').remove();
        lineG.selectAll('path').remove();

        let line = lineG.append('path')
                .datum(lineArray);

        let area = areaG.append('path')
                .datum(lineArray);

        line.attr('class', 'line')
            .attr('d', prepareTransition)
            .transition().duration(1000)
            .attr('d', drawLine);

        let focus = areaG.append("g")
            .attr("class", ".line-chart-growth__focus")
            .style("display", "none");


        focus.append("circle")
            .attr("r", 5);

        function mousemove() {
            let x0 = xScale.invert(d3.mouse(this)[0]),
                i = bisectDate(lineDataArrayMap, x0, 1),
                d0 = lineDataArrayMap[i - 1],
                d1 = lineDataArrayMap[i],
                d = x0 - d0.date > d1.date - x0 ? d1 : d0;
            focus.attr("transform", "translate(" + x(d.date) + "," + y(d.value) + ")");
            tooltip.html(createTooltip(d, currencyId))
                .style('left', () => `${x(d.date)}px`)
                .style('top', () => `${(y(d.value))}px`)
                .style('display', 'block');
        }

        yaxisG.attr('class', 'line-chart-growth__yaxis line-chart-growth__axis')
            .attr('transform', 'translate('+leftPadding*2+',0)')
            .call(yAxis);

        xaxisG.attr('class', 'line-chart-growth__xaxis line-chart-growth__axis')
            .attr('transform', 'translate(0,' + (height-PADDING) + ')')
            .call(xAxis)
            .selectAll('.line-chart-growth__axis .tick text')
                .call(function(tick){
                    tick.each(function(){
                        let self = d3.select(this);
                        let s = self.text().split(' ');
                        self.text('');
                        self.append("tspan")
                            .attr("x", 0)
                            .attr("dy",".8em")
                            .text(s[0]);
                        self.append("tspan")
                            .attr("x", 0)
                            .attr("dy","12")
                            .text(s[1]);
                    });
                });

        xaxisG.selectAll('.line-chart-growth__xaxis text')
            .attr('transform', 'translate(0, 10)');

        yaxisG.selectAll('.line-chart-growth__yaxis text')
            .attr('transform', `translate(-${leftPadding-15}, 0)`);

        area.attr('class', 'area')
            .on("mouseover", function() { focus.style("display", null); })
            .on("mouseout", function() {
                focus.style("display", "none");
                tooltip.style('display', 'none');
            })
            .on("mousemove", mousemove)
            .attr('d', prepareTransition)
            .transition().duration(1000)
            .attr('d', drawArea);

    }
}

class LineChartGrowth extends React.Component {

    constructor(props) {
        super(props);
        this.setNode = this.setNode.bind(this);
    }

    componentDidMount() {
        if (this.props.lineData){
            d3.select(this.nodeReference)
                .call(blackBox(this.props));
        }
    }

    componentDidUpdate(old) {
        let difference = reduce(this.props, (result, value, key) => {
            return isEqual(value, old[key]) ? result : result.concat(key);
        }, []);

        if (difference.length === 0) { return; }

        d3.select(this.nodeReference)
            .call(blackBox(this.props));
    }

    setNode(ref) {
        this.nodeReference = ref;
    }

    render() {
        return (
            <div ref={this.setNode} className="line-chart-growth">

                <svg width={this.props.width}
                    height={this.props.height}
                    shapeRendering="geometricPrecision"
                >
                    <defs>
                        <linearGradient id="linear" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop className="line-chart-growth__stop1" offset="0%"/>
                            <stop className="line-chart-growth__stop2" offset="100%"/>
                        </linearGradient>
                    </defs>

                    <g className="line-chart-growth__grid"></g>
                    <g className="line-chart-growth__xaxis"></g>
                    <g className="line-chart-growth__yaxis"></g>
                    <g className="line-chart-growth__line">
                        <path></path>
                    </g>
                    <g className="line-chart-growth__area">
                        <path></path>
                    </g>
                    <g className="line-chart-growth__circles"></g>
                </svg>
                <div className="line-chart-growth__tooltip"></div>

            </div>
        );
    }
}

LineChartGrowth.propTypes = {
    lineData: PropTypes.object.isRequired,
    pointsData: PropTypes.object.isRequired,
    width: PropTypes.string.isRequired,
    height: PropTypes.string.isRequired,
    currencyId: PropTypes.string.isRequired
};

module.exports = LineChartGrowth;
